/**
 * Remonte un plantage de l'interface au backend.
 *
 * Sans cela, une erreur rencontrée par un client ne laissait qu'un
 * `console.error` dans SON navigateur : l'équipe ne pouvait savoir ni ce qui
 * cassait, ni chez qui, ni à quelle fréquence. On réparait à l'aveugle, et on
 * demandait aux utilisateurs de désactiver leurs extensions faute de mieux.
 *
 * L'envoi est délibérément silencieux et sans jeton : une page qui vient de
 * planter peut avoir perdu sa session, et exiger une authentification ferait
 * perdre précisément les rapports les plus graves. Un échec d'envoi n'est jamais
 * remonté à l'utilisateur : il a déjà un problème, ce n'est pas le moment de lui
 * en annoncer un second.
 */

const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'http://localhost:8000/land/api/v1';

export interface ClientErrorReport {
  message: string;
  stack?: string;
  componentStack?: string;
  userId?: string;
}

/** Borne la taille des champs : un rapport ne doit pas devenir un déversoir. */
function clamp(value: string | undefined, max: number): string | undefined {
  if (!value) return undefined;
  return value.length > max ? value.slice(0, max) : value;
}

export function reportClientError(report: ClientErrorReport): void {
  try {
    const body = JSON.stringify({
      app: 'wanzo-land',
      message: clamp(report.message, 500) || 'Erreur inconnue',
      stack: clamp(report.stack, 4000),
      componentStack: clamp(report.componentStack, 4000),
      url: clamp(window.location.href, 500),
      userAgent: clamp(navigator.userAgent, 300),
      userId: clamp(report.userId, 100),
    });

    // `sendBeacon` survit à un rechargement ou à la fermeture de l'onglet, ce
    // qui est exactement la situation d'un plantage. Repli sur `fetch` quand il
    // n'est pas disponible.
    const url = `${API_BASE_URL}/client-errors`;
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      return;
    }
    void fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {
      // Le signalement est un bonus : son échec ne doit rien casser.
    });
  } catch {
    // Idem : jamais d'erreur en cascade depuis le rapport d'erreur.
  }
}
